import EmberService from '@ember/service';

export default class ViewSettingsManagerService extends EmberService {
	private settings: Record<string, string> = {};

	load(settingsName: string): string;
	load(settingsName: string, isJSON: false): string;
	load<T extends object = Record<string, unknown>>(
		settingsName: string,
		isJSON: true,
	): Partial<T> | null;
	load(settingsName: string, isJSON = false): object | string | null {
		const alreadyCached = Object.keys(this.settings).includes(settingsName);
		let storedValue: string | null | undefined;

		if (alreadyCached) {
			storedValue = this.settings[settingsName];
		} else {
			// load from localstorage
			try {
				storedValue = localStorage.getItem(settingsName);
			} catch (_e) {
				// just ignore, not important enough to warn user
			}
		}

		let settingsValue: unknown = storedValue;
		if (storedValue && isJSON) {
			try {
				settingsValue = JSON.parse(storedValue);
			} catch (_e) {
				settingsValue = {};
			}
		}

		if (!alreadyCached) {
			this.cacheLocally(settingsName, settingsValue, isJSON);
		}

		return settingsValue as object | string | null;
	}

	save(settingsName: string, settingsValue: object | string, isJSON: boolean): void {
		const finalValue = this.cacheLocally(settingsName, settingsValue, isJSON);

		// Safari in Private Browsing Mode supports localStorage but all calls to setItem throw QuotaExceededError
		try {
			localStorage.setItem(settingsName, finalValue);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	remove(settingsName: string): void {
		delete this.settings[settingsName];

		// Safari in Private Browsing Mode supports localStorage but all calls to setItem throw QuotaExceededError
		try {
			localStorage.removeItem(settingsName);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	private cacheLocally(settingsName: string, settingsValue: unknown, isJSON: boolean): string {
		const finalValue = (isJSON ? JSON.stringify(settingsValue) : settingsValue) as string;
		this.settings[settingsName] = finalValue;

		return finalValue;
	}
}

declare module '@ember/service' {
	interface Registry {
		'view-settings-manager': ViewSettingsManagerService;
	}
}
